import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Lock, ArrowRight, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      // Trim both fields — mobile keyboards often append invisible whitespace
      const cleanEmail = email.trim().toLowerCase();
      const cleanPassword = password.trim();
      const { data } = await axios.post(
        `${API}/api/auth/login`,
        { email: cleanEmail, password: cleanPassword }
      );
      localStorage.setItem('admin_token', data.token);
      localStorage.setItem('admin_user', JSON.stringify(data.user));
      navigate('/admin');
    } catch (err) {
      // Detailed diagnostics so we know *exactly* what's failing
      let msg;
      if (err.response) {
        // API returned a response with status code
        const detail = err.response.data?.detail;
        msg = typeof detail === 'string'
          ? detail
          : `Server returned ${err.response.status}. Please try again.`;
      } else if (err.request) {
        // Request was made but no response received (network/CORS/blocked)
        msg = `Network error — could not reach the server. Check connection or try a different browser. [code: ${err.code || 'no_response'}]`;
      } else {
        msg = `Client error: ${err.message || 'Unknown error.'}`;
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-cyan-500/10 flex items-center justify-center mx-auto mb-4">
            <Lock className="h-7 w-7 text-cyan-400" />
          </div>
          <h1 className="text-2xl font-bold text-white" data-testid="admin-login-title">Admin Dashboard</h1>
          <p className="text-slate-400 text-sm mt-2">Sign in to manage leads and analytics</p>
        </div>

        <form onSubmit={handleSubmit} data-testid="admin-login-form" className="bg-slate-900 rounded-2xl p-6 sm:p-8 border border-slate-800 space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm" data-testid="login-error">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <div>
            <label className="text-slate-400 text-sm mb-2 block">Email</label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@culturealign.com"
              required
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              inputMode="email"
              data-testid="admin-email-input"
              className="bg-slate-950 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500"
            />
          </div>

          <div>
            <label className="text-slate-400 text-sm mb-2 block">Password</label>
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                required
                autoComplete="current-password"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                data-testid="admin-password-input"
                className="bg-slate-950 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500 pr-11"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                data-testid="toggle-password-visibility"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-cyan-400 transition-colors"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <div className="mt-2 text-right">
              <Link
                to="/admin/forgot-password"
                className="text-xs text-cyan-400 hover:text-cyan-300 transition-colors"
                data-testid="forgot-password-link"
              >
                Forgot password?
              </Link>
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            data-testid="admin-login-button"
            className="w-full bg-white text-slate-950 hover:bg-slate-100 font-semibold py-5"
          >
            {loading ? 'Signing in...' : 'Sign In'}
            {!loading && <ArrowRight className="ml-2 h-4 w-4" />}
          </Button>
        </form>

        <p className="text-center text-slate-500 text-xs mt-6">
          <a href="/" className="hover:text-white transition-colors">Back to website</a>
        </p>
      </div>
    </div>
  );
}
